import { useCallback, useState } from 'preact/hooks';
import { usePaginatedList } from '../../hooks/use-paginated-list';
import { getIngredients } from '../../api/ingredients';
import { Card } from '../../components/card';
import { Table, type Column } from '../../components/table';
import { Pagination } from '../../components/pagination';
import { Input } from '../../components/input';
import { Checkbox } from '../../components/checkbox';
import { Button } from '../../components/button';
import { Badge } from '../../components/badge';
import { Spinner } from '../../components/spinner';
import { EmptyState } from '../../components/empty-state';
import { Message } from '../../components/message';
import { FlagHeatmap } from '../../components/flag-heatmap';
import type { Ingredient } from '../../api/types';

interface IngredientTableProps {
  onSelect: (id: number) => void;
  onImpact: (id: number) => void;
}

const LIMIT = 20;

function allergenCount(i: Ingredient): number {
  const fields: (keyof Ingredient)[] = [
    'contains_gluten', 'contains_dairy', 'contains_nuts', 'contains_peanuts',
    'contains_soy', 'contains_fish', 'contains_shellfish', 'contains_sesame',
    'contains_egg', 'contains_alcohol', 'contains_mustard', 'contains_celery',
    'contains_lupin', 'contains_mollusks', 'contains_sulfites', 'contains_wheat',
    'contains_corn',
  ];
  return fields.filter((f) => i[f] === 1).length;
}

function boolBadge(val: number | null, label: string) {
  if (val === 1) return <Badge label={label} variant="warn" />;
  return null;
}

export function IngredientTable({ onSelect, onImpact }: IngredientTableProps) {
  const [autoLearned, setAutoLearned] = useState(false);

  const fetcher = useCallback(async (page: number, search: string) => {
    const res = await getIngredients({
      page,
      limit: LIMIT,
      search: search || undefined,
      autoLearned: autoLearned || undefined,
    });
    return { items: res.ingredients, total: res.total };
  }, [autoLearned]);

  const list = usePaginatedList<Ingredient>(fetcher);

  const columns: Column<Ingredient>[] = [
    { header: 'Name', key: 'canonical_name', sortable: true },
    { header: 'E-No', key: 'e_number', sortable: true, render: (r) => r.e_number ?? '\u2014' },
    {
      header: 'Flags',
      key: 'flags',
      render: (r) => <FlagHeatmap ingredient={r} />,
    },
    {
      header: 'Halal Override',
      key: 'halal_status_override',
      render: (r) => {
        if (!r.halal_status_override) return '\u2014';
        const v = r.halal_status_override === 'halal' ? 'ok'
          : r.halal_status_override === 'haram' ? 'error' : 'warn';
        return <Badge label={r.halal_status_override} variant={v} />;
      },
    },
    {
      header: 'GI Category',
      key: 'glycemic_index_category',
      render: (r) => r.glycemic_index_category ?? '\u2014',
    },
    {
      header: 'NOVA',
      key: 'nova_group',
      render: (r) => r.nova_group != null ? String(r.nova_group) : '\u2014',
    },
    {
      header: 'Source',
      key: 'source_flags',
      render: (r) => (
        <span style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {boolBadge(r.is_animal_derived, 'Animal')}
          {boolBadge(r.is_plant_derived, 'Plant')}
          {boolBadge(r.is_synthetic, 'Synth')}
          {r.source_ambiguous === 1 && <Badge label="Ambiguous" variant="info" />}
          {!r.is_animal_derived && !r.is_plant_derived && !r.is_synthetic && !r.source_ambiguous && '\u2014'}
        </span>
      ),
    },
    {
      header: 'Allergens',
      key: 'allergens',
      render: (r) => {
        const count = allergenCount(r);
        return count > 0 ? <Badge label={String(count)} variant="warn" /> : '0';
      },
    },
    {
      header: '',
      key: 'actions',
      render: (r) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => { onImpact(r.id); }}
        >
          Impact
        </Button>
      ),
    },
  ];

  return (
    <Card>
      <div class="toolbar">
        <Input
          value={list.search}
          onChange={list.setSearch}
          placeholder="Search by name or E-number..."
          icon="search"
        />
        <Checkbox
          checked={autoLearned}
          onChange={(v) => {
            setAutoLearned(v);
            list.setSearch(list.search);
          }}
          label="Auto-learned only"
          id="filter-auto-learned"
        />
      </div>

      {list.loading ? (
        <Spinner text="Loading ingredients" centered />
      ) : list.error ? (
        <Message text={list.error} type="error" />
      ) : list.items.length === 0 ? (
        <EmptyState icon="search" message="No ingredients found" />
      ) : (
        <>
          <Table
            columns={columns}
            rows={list.items}
            onRowClick={(row) => onSelect(row.id)}
          />
          <Pagination
            page={list.page}
            total={list.total}
            limit={LIMIT}
            onPageChange={list.setPage}
          />
        </>
      )}
    </Card>
  );
}
